import { createFileRoute } from "@tanstack/react-router"
import { usePostHog } from "posthog-js/react"

import mockBackground from "@/assets/features/trails/mock-background.avif"
import { agents, type AgentId } from "@/lib/agents"
import { marketingPages } from "@/lib/marketing-pages"
import { warmSignInIntent } from "@/lib/navigationWarmup"
import { seoMeta } from "@/lib/seo"
import { ResponsiveImage } from "@/routes/-components/site/ResponsiveImage"
import { SiteSection } from "@/routes/-components/site/SiteSection"
import { FeatureBenefitsSection } from "@/routes/features/-components/FeatureBenefitsSection"
import { FeatureCalloutSection } from "@/routes/features/-components/FeatureCalloutSection"
import { FeatureTitleSection } from "@/routes/features/-components/FeatureTitleSection"
import { SessionsBlogLinksSection } from "@/routes/features/sessions/-components/SessionsBlogLinksSection"

import { TrailsDemoFrame } from "./-components/TrailsDemoFrame"
import { TrailsFeatures, TrailsHowItWorks } from "./-components/TrailsFeatures"

export const Route = createFileRoute("/features/trails/")({
  head: () =>
    seoMeta({
      ...marketingPages["/features/trails"],
      url: "https://entire.com/features/trails",
      image: "/images/og/trails.png",
      imageAlt: "Entire social card for Trails: the word Trails over a snowy mountain canyon.",
    }),
  component: TrailsPage,
})
const BENEFITS = [
  {
    title: "Keep the context.",
    description:
      "Start with the goal, decisions, and discussion. Give everyone the same place to understand the work.",
  },
  {
    title: "Connect the changes.",
    description:
      "Bring branches and repositories together under one Trail, with the sessions behind each change.",
  },
  {
    title: "Know what needs attention.",
    description: "See review findings, checks, and approvals together, so the next step is clear.",
  },
]
function TrailsPage() {
  const posthog = usePostHog()
  return (
    <div>
      <FeatureTitleSection
        badge="Trails"
        secondaryBadge="New"
        actionLabel="Get started"
        eventName="trails_title_cta_clicked"
        title={
          <>
            Take work from idea
            <br />
            <span className="text-accent">to merge.</span>
          </>
        }
        description="Bring context, code, and reviews into one place. Connect changes across repositories, follow the sessions behind them, and see what’s needed to ship."
      />
      <FeatureBenefitsSection benefits={BENEFITS} />
      <SiteSection
        id="explore-trails"
        innerClassName="relative isolate h-[700px] overflow-hidden bg-(--neutral-150) p-3 sm:p-8 lg:h-[770px] lg:p-16"
      >
        <ResponsiveImage
          src={mockBackground}
          loading="eager"
          alt=""
          aria-hidden="true"
          width={2688}
          height={1792}
          pictureClassName="absolute inset-0 size-full"
          className="size-full object-cover"
        />
        <div className="relative flex size-full flex-col overflow-hidden rounded-lg bg-base shadow-[0_9px_31px_rgb(0_0_0/0.12),0_3px_3px_rgb(0_0_0/0.1)]">
          <TrailsDemoFrame />
        </div>
      </SiteSection>
      <FeatureCalloutSection
        lead="One place for the work."
        emphasis="From idea to merge."
        description="Start with shared context. Connect the changes as you build. Keep decisions, reviews, and progress attached to the work all the way through."
      />
      <TrailsFeatures />
      <TrailsHowItWorks />
      <SiteSection innerClassName="relative isolate flex min-h-[494px] flex-col items-center overflow-hidden px-6 pt-[153px] pb-20 text-center text-white">
        <ResponsiveImage
          src={mockBackground}
          alt=""
          aria-hidden="true"
          width={2688}
          height={1792}
          pictureClassName="absolute inset-0 -z-10 size-full"
          className="size-full object-cover object-bottom brightness-60"
        />
        <h2 className="font-headline text-[40px] leading-[46px] font-semibold">
          Get started with Trails
        </h2>
        <a
          href="/login"
          onMouseEnter={warmSignInIntent}
          onFocus={warmSignInIntent}
          onClick={() => posthog.capture("trails_footer_cta_clicked")}
          className="mt-8 flex h-12 items-center gap-1 rounded-lg bg-white px-5 text-sm font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-4"
        >
          Get started today
        </a>
        <p className="mt-8 text-sm leading-6">Works with any agent</p>
        <div className="mt-4 flex gap-4">
          {(
            [
              "claude",
              "codex",
              "gemini",
              "copilot",
              "cursor",
              "opencode",
              "droid",
            ] satisfies AgentId[]
          ).map((id) => (
            <img
              key={id}
              src={agents[id].logoUrl}
              alt={agents[id].label}
              width={20}
              height={20}
              className="size-5 brightness-0 invert"
            />
          ))}
        </div>
      </SiteSection>
      <SessionsBlogLinksSection />
    </div>
  )
}
